import { create } from 'zustand';
import type {
  AlarmSite,
  AlarmPanelWithSite,
  AlarmEventWithRelations,
  AlarmPanelStatus,
  AlarmEventSeverity,
} from '@asi/shared';
import * as alarmsService from '@/services/alarms';

interface AlarmsState {
  // Data
  sites: AlarmSite[];
  panels: AlarmPanelWithSite[];
  events: AlarmEventWithRelations[];
  unackedEvents: AlarmEventWithRelations[];
  
  // UI State
  selectedSite: AlarmSite | null;
  selectedPanel: AlarmPanelWithSite | null;
  loading: boolean;
  syncing: boolean;
  
  // Filters
  filterStatus: AlarmPanelStatus | 'ALL';
  filterSeverity: AlarmEventSeverity | 'ALL';
  search: string;
  
  // Counts
  panelCounts: Record<string, number>;
  eventCounts: Record<string, number>;

  // Actions
  fetchSites: () => Promise<void>;
  fetchPanels: () => Promise<void>;
  fetchEvents: (limit?: number) => Promise<void>;
  fetchUnackedEvents: () => Promise<void>;
  syncFromHpp: () => Promise<{ sites: number; panels: number }>;
  
  selectSite: (site: AlarmSite | null) => void;
  selectPanel: (panel: AlarmPanelWithSite | null) => void;
  setFilterStatus: (status: AlarmPanelStatus | 'ALL') => void;
  setFilterSeverity: (severity: AlarmEventSeverity | 'ALL') => void;
  setSearch: (search: string) => void;
  
  acknowledgeEvent: (eventId: string) => Promise<void>;
  armPanel: (serial: string, mode?: 'away' | 'stay' | 'instant') => Promise<void>;
  disarmPanel: (serial: string) => Promise<void>;
  
  subscribeToEvents: () => () => void;
  subscribeToPanels: () => () => void;
  subscribeToSites: () => () => void;

  // Derived
  filteredPanels: () => AlarmPanelWithSite[];
  filteredEvents: () => AlarmEventWithRelations[];
}

export const useAlarmsStore = create<AlarmsState>((set, get) => ({
  // Initial state
  sites: [],
  panels: [],
  events: [],
  unackedEvents: [],
  selectedSite: null,
  selectedPanel: null,
  loading: false,
  syncing: false,
  filterStatus: 'ALL',
  filterSeverity: 'ALL',
  search: '',
  panelCounts: { ONLINE: 0, OFFLINE: 0, ALARM: 0, FAULT: 0 },
  eventCounts: { INFO: 0, WARNING: 0, ALARM: 0, CRITICAL: 0 },

  // Fetch actions
  fetchSites: async () => {
    if (get().sites.length > 0) return; // already loaded
    set({ loading: true });
    try {
      const sites = await alarmsService.fetchSites();
      set({ sites });
    } catch (e) {
      console.error('[fetchSites]', e);
    } finally {
      set({ loading: false });
    }
  },

  fetchPanels: async () => {
    // Stale-while-revalidate: only show spinner on first load
    if (get().panels.length === 0) set({ loading: true });
    try {
      const [panels, panelCounts] = await Promise.all([
        alarmsService.fetchPanels(),
        alarmsService.countPanelsByStatus(),
      ]);
      set({ panels, panelCounts });
    } catch (e) {
      console.error('[fetchPanels]', e);
    } finally {
      set({ loading: false });
    }
  },

  fetchEvents: async (limit = 100) => {
    // Stale-while-revalidate: only show spinner on first load
    if (get().events.length === 0) set({ loading: true });
    try {
      const [events, eventCounts] = await Promise.all([
        alarmsService.fetchRecentEvents(limit),
        alarmsService.countEventsBySeverity(),
      ]);
      set({ events, eventCounts });
    } finally {
      set({ loading: false });
    }
  },

  fetchUnackedEvents: async () => {
    const unackedEvents = await alarmsService.fetchUnacknowledgedEvents();
    set({ unackedEvents });
  },

  syncFromHpp: async () => {
    set({ syncing: true });
    try {
      // Runs on the gateway (which also syncs automatically every few minutes).
      const result = await alarmsService.runHppSync();

      // Refresh data after sync
      await get().fetchSites();
      await get().fetchPanels();

      // Manual sync also refreshes partition states of online panels (slow, background).
      alarmsService.refreshPanelSubsystems(
        get().panels.filter((p) => p.status === 'ONLINE').map((p) => p.serial_number),
      );

      return {
        sites: result.sites.inserted + result.sites.updated,
        panels: result.panels.inserted + result.panels.updated,
      };
    } finally {
      set({ syncing: false });
    }
  },

  // Selection actions
  selectSite: (site) => set({ selectedSite: site }),
  selectPanel: (panel) => set({ selectedPanel: panel }),
  
  // Filter actions
  setFilterStatus: (filterStatus) => set({ filterStatus }),
  setFilterSeverity: (filterSeverity) => set({ filterSeverity }),
  setSearch: (search) => set({ search }),

  // Panel actions
  acknowledgeEvent: async (eventId) => {
    await alarmsService.acknowledgeEvent(eventId);
    await get().fetchEvents();
    await get().fetchUnackedEvents();
  },

  armPanel: async (serial, mode = 'away') => {
    await alarmsService.armPanel(serial, mode);
    await get().fetchPanels();
  },

  disarmPanel: async (serial) => {
    await alarmsService.disarmPanel(serial);
    await get().fetchPanels();
  },

  // Subscriptions
  subscribeToEvents: () => {
    return alarmsService.subscribeToAlarmEvents((event) => {
      set((state) => ({
        events: [{ ...event, panel: {} as any, zone: null, event_type: null }, ...state.events].slice(0, 100),
      }));
      // Refresh unacked count
      get().fetchUnackedEvents();
    });
  },

  subscribeToPanels: () => {
    return alarmsService.subscribeToPanelChanges(
      // Handle INSERT - add new panel to state
      (panel) => {
        set((state) => {
          // Check if panel already exists (avoid duplicates)
          if (state.panels.some((p) => p.id === panel.id)) {
            return state;
          }
          // Add new panel and update counts
          const newPanels = [{ ...panel, site: null } as AlarmPanelWithSite, ...state.panels];
          const panelCounts = { ...state.panelCounts };
          panelCounts[panel.status] = (panelCounts[panel.status] || 0) + 1;
          return { panels: newPanels, panelCounts };
        });
      },
      // Handle UPDATE - update existing panel
      (panel) => {
        set((state) => ({
          panels: state.panels.map((p) =>
            p.id === panel.id ? { ...p, ...panel } : p
          ),
          // Keep the drawer in sync with real-time changes
          selectedPanel: state.selectedPanel?.id === panel.id
            ? { ...state.selectedPanel, ...panel }
            : state.selectedPanel,
        }));
      }
    );
  },

  subscribeToSites: () => {
    return alarmsService.subscribeToSiteAccountChanges(() => {
      void get().fetchPanels();
    });
  },

  // Derived state
  filteredPanels: () => {
    const { panels, filterStatus, search, selectedSite } = get();
    
    return panels.filter((p) => {
      // Filter by status
      if (filterStatus !== 'ALL' && p.status !== filterStatus) return false;
      
      // Filter by selected site
      if (selectedSite && p.site_id !== selectedSite.id) return false;
      
      // Filter by search
      if (search) {
        const q = search.toLowerCase();
        const matchesName = p.name.toLowerCase().includes(q);
        const matchesSerial = p.serial_number.toLowerCase().includes(q);
        const matchesSite = p.site?.name?.toLowerCase().includes(q);
        if (!matchesName && !matchesSerial && !matchesSite) return false;
      }
      
      return true;
    });
  },

  filteredEvents: () => {
    const { events, filterSeverity, search, selectedPanel } = get();
    
    return events.filter((e) => {
      // Filter by severity
      if (filterSeverity !== 'ALL' && e.severity !== filterSeverity) return false;
      
      // Filter by selected panel
      if (selectedPanel && e.panel_id !== selectedPanel.id) return false;
      
      // Filter by search
      if (search) {
        const q = search.toLowerCase();
        const matchesPanel = e.panel?.name?.toLowerCase().includes(q);
        const matchesZone = e.zone?.name?.toLowerCase().includes(q);
        const matchesCode = e.cid_code.includes(q);
        if (!matchesPanel && !matchesZone && !matchesCode) return false;
      }
      
      return true;
    });
  },
}));
